import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../../utils";
import { RootState } from "../../../redux/store";
import { GetChargesOfCompanyResponse } from "@qivia/schemas-ev-charging";
import { ElectricCharge } from "./electricChargingAPI";

export interface ElectricChargingState {
  electricChargesStatus: QueryStatus;
  electricCharges: ElectricCharge[];
  reimburseChargesStatus: QueryStatus;
}

const initialState: ElectricChargingState = {
  electricChargesStatus: "idle",
  electricCharges: [],
  reimburseChargesStatus: "idle",
};

export const getElectricChargesAsync = createAsyncThunk(
  "getElectricChargesAsync/call",
  async (_: void, thunkAPI): Promise<ElectricCharge[]> => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    const response = await axios.get<GetChargesOfCompanyResponse>(
      `electric_charges/company/${company.uuid}`,
    );
    return response.data.map((charge) => {
      const durationInHours =
        charge.startAt && charge.stopAt
          ? (new Date(charge.stopAt).getTime() -
              new Date(charge.startAt).getTime()) /
            (1000 * 60 * 60)
          : null;
      return {
        uuid: charge.uuid,
        vehicleUuid: charge.vehicleUuid,
        driverUuid: charge.driverUuid,
        registrationNumber: charge.registrationNumber,
        driverName: charge.driverName,
        type: charge.type,
        startAt: charge.startAt,
        stopAt: charge.stopAt,
        totalEnergy: charge.totalEnergy,
        reimbursementAmount: charge.reimbursementAmount,
        identifier: null,
        vehicleReference: null,
        locationName: null,
        locationAddress: null,
        locationCity: null,
        locationPostalCode: null,
        totalCost: null,
        refundStatus: null,
        reimbursedAt: null,
        totalTime: durationInHours,
        chargingTime: durationInHours,
        totalParkingTime: null,
      };
    });
  },
);

export const reimburseChargesAsync = createAsyncThunk(
  "reimburseCharges/call",
  async (chargeUuids: string[], thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    await axios.post(`electric_charges/reimburse`, { chargeUuids });
  },
);

export const electricChargingSlice = createSlice({
  name: "electricCharging",
  initialState,
  reducers: {
    resetElectricChargesStatus(state) {
      state.electricChargesStatus = "idle";
    },
    resetReimburseChargesStatus(state) {
      state.reimburseChargesStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getElectricChargesAsync.pending, (state) => {
        state.electricChargesStatus = "processing";
      })
      .addCase(getElectricChargesAsync.fulfilled, (state, action) => {
        state.electricChargesStatus = "success";
        state.electricCharges = action.payload;
      })
      .addCase(getElectricChargesAsync.rejected, (state) => {
        state.electricChargesStatus = "failed";
      })
      .addCase(reimburseChargesAsync.pending, (state) => {
        state.reimburseChargesStatus = "processing";
      })
      .addCase(reimburseChargesAsync.fulfilled, (state) => {
        state.reimburseChargesStatus = "success";
      })
      .addCase(reimburseChargesAsync.rejected, (state) => {
        state.reimburseChargesStatus = "failed";
      });
  },
});

export const selectElectricChargesStatus = (state: RootState) =>
  state.electricCharging.electricChargesStatus;

export const selectElectricCharges = (state: RootState) =>
  state.electricCharging.electricCharges;

export const selectReimburseChargesStatus = (state: RootState) =>
  state.electricCharging.reimburseChargesStatus;

export default electricChargingSlice.reducer;
